/**
 * simulation.ts
 *
 * Centralized configuration for simulation parameters
 * Replaces hard-coded values throughout the codebase
 */

export const SIMULATION_CONFIG = {
  // Financial calculation constants
  CRISIS_THRESHOLD: -0.15, // 15% loss threshold for crisis detection

  // Erfolgs-Definition der Ruhestandssimulation (verfeinert):
  // Ein Portfolio gilt nur dann als Erfolg, wenn es das Zielalter erreicht UND im
  // ZIELJAHR (letztem Eintrag) mindestens diesen Anteil der inflationsadjustierten
  // Zielentnahme tatsächlich bedienen konnte. Bezieht sich bewusst auf die ENTNAHME-
  // Bedienung im Zieljahr (gesamtentnahme / zielentnahme), NICHT auf den Endsaldo:
  // Volle Entnahme + Endsaldo 0 => Erfolg; nur Teil-Entnahme im Zieljahr => Misserfolg.
  SUCCESS_WITHDRAWAL_THRESHOLD: 0.95, // >= 95% der Zieljahr-Entnahme muss bedient sein

  // Entnahmerate-Empfehlungs-Schwelle:
  // Für die „optimale Entnahmerate" wird die HÖCHSTE getestete Rate empfohlen,
  // deren Erfolgsquote >= dieser Schwelle liegt. Dies unterscheidet sich von
  // SUCCESS_WITHDRAWAL_THRESHOLD, welches für die Strategie-Erfolgs-Definition
  // verwendet wird. Fallback: Falls keine Rate die Schwelle erreicht, wird die
  // Rate mit der höchsten Erfolgsquote empfohlen, mit Hinweis im UI.
  OPTIMAL_WITHDRAWAL_SUCCESS_THRESHOLD: 0.95, // >= 95% Erfolgsquote für Empfehlung

  // Performance calculation defaults
  DEFAULT_AVERAGE_INFLATION_RATE: 2.0, // 2% average inflation assumption
  DEFAULT_RISK_FREE_RATE: 1.0, // 1% risk-free rate assumption

  // Drawdown calculation adjustments
  CASHFLOW_REDUCTION_BASE: 0.15, // Base 15% reduction for cashflow impact
  CASHFLOW_REDUCTION_MAX: 0.25, // Maximum 25% reduction for cashflow impact
  CASHFLOW_REDUCTION_YEARS_FACTOR: 0.005, // Factor per year above 20 years

  // Performance adjustment factors
  PERFORMANCE_ADJUSTMENT: {
    POOR_RETURN_THRESHOLD: 4, // Returns below 4% considered poor
    GOOD_RETURN_THRESHOLD: 8, // Returns above 8% considered good
    POOR_MULTIPLIER: 1.2, // Increase drawdown for poor performance
    GOOD_MULTIPLIER: 0.8, // Decrease drawdown for good performance
  },

  // Monte Carlo configuration
  MONTE_CARLO: {
    DEFAULT_ITERATIONS: 100,
    POOL_SIZE: 4, // Number of engine instances for pooling
    BATCH_SIZE_DIVISOR: 4, // Divide iterations by this for batch processing
    PROGRESS_UPDATE_FREQUENCY: 100, // Update progress every N iterations
  },

  // Cache management
  CACHE: {
    MAX_CACHE_SIZE: 100, // Maximum number of cached calculations
    CACHE_TTL_MS: 30 * 60 * 1000, // 30 minutes cache time-to-live
  },

  // Default simulation options
  DEFAULTS: {
    selectedKapitalbedarf: 'ohne-zinseszins',
    inflationOption: 'parameter-inflation',
    investmentReturnOption: 'konstante-rendite',
    tagesgeldOption: 'keine-rendite',
    zinstopfOption: 'keine-rendite',
    isLogarithmicScale: false,
  },

  // UI update thresholds
  UI: {
    DEBOUNCE_MS: 300, // Debounce user input
    LONG_RUNNING_THRESHOLD_MS: 5000, // Show progress after 5 seconds
  },

  // Financial metrics thresholds
  METRICS: {
    EXCELLENT_SHARPE_RATIO: 1.5,
    GOOD_SHARPE_RATIO: 1.0,
    ACCEPTABLE_SHARPE_RATIO: 0.5,

    HIGH_VOLATILITY_THRESHOLD: 20, // 20% volatility considered high
    MODERATE_VOLATILITY_THRESHOLD: 12, // 12% volatility considered moderate

    SUCCESS_RATE_EXCELLENT: 95, // 95%+ success rate excellent
    SUCCESS_RATE_GOOD: 85, // 85%+ success rate good
    SUCCESS_RATE_ACCEPTABLE: 70, // 70%+ success rate acceptable
  },
} as const;

// Type-safe configuration access
export type SimulationConfig = typeof SIMULATION_CONFIG;

// Helper functions for common calculations
export const SimulationUtils = {
  isCrisisYear: (investitionstopfRate: number): boolean => {
    return investitionstopfRate < SIMULATION_CONFIG.CRISIS_THRESHOLD;
  },

  getMarktSituation: (investitionstopfRate: number): 'Hoch' | 'Krise' => {
    return SimulationUtils.isCrisisYear(investitionstopfRate) ? 'Krise' : 'Hoch';
  },

  calculateCashflowReduction: (years: number): number => {
    const { CASHFLOW_REDUCTION_BASE, CASHFLOW_REDUCTION_MAX, CASHFLOW_REDUCTION_YEARS_FACTOR } =
      SIMULATION_CONFIG;
    return Math.min(
      CASHFLOW_REDUCTION_MAX,
      CASHFLOW_REDUCTION_BASE + (years - 20) * CASHFLOW_REDUCTION_YEARS_FACTOR
    );
  },

  getPerformanceAdjustment: (returnRate: number): number => {
    const { POOR_RETURN_THRESHOLD, GOOD_RETURN_THRESHOLD, POOR_MULTIPLIER, GOOD_MULTIPLIER } =
      SIMULATION_CONFIG.PERFORMANCE_ADJUSTMENT;

    if (returnRate < POOR_RETURN_THRESHOLD) {return POOR_MULTIPLIER;}
    if (returnRate > GOOD_RETURN_THRESHOLD) {return GOOD_MULTIPLIER;}
    return 1.0; // Neutral adjustment
  },

  classifySharpeRatio: (sharpe: number): 'excellent' | 'good' | 'acceptable' | 'poor' => {
    const { EXCELLENT_SHARPE_RATIO, GOOD_SHARPE_RATIO, ACCEPTABLE_SHARPE_RATIO } =
      SIMULATION_CONFIG.METRICS;

    if (sharpe >= EXCELLENT_SHARPE_RATIO) {return 'excellent';}
    if (sharpe >= GOOD_SHARPE_RATIO) {return 'good';}
    if (sharpe >= ACCEPTABLE_SHARPE_RATIO) {return 'acceptable';}
    return 'poor';
  },

  classifyVolatility: (volatility: number): 'low' | 'moderate' | 'high' => {
    const { HIGH_VOLATILITY_THRESHOLD, MODERATE_VOLATILITY_THRESHOLD } = SIMULATION_CONFIG.METRICS;

    if (volatility >= HIGH_VOLATILITY_THRESHOLD) {return 'high';}
    if (volatility >= MODERATE_VOLATILITY_THRESHOLD) {return 'moderate';}
    return 'low';
  },

  classifySuccessRate: (successRate: number): 'excellent' | 'good' | 'acceptable' | 'poor' => {
    const { SUCCESS_RATE_EXCELLENT, SUCCESS_RATE_GOOD, SUCCESS_RATE_ACCEPTABLE } =
      SIMULATION_CONFIG.METRICS;

    if (successRate >= SUCCESS_RATE_EXCELLENT) {return 'excellent';}
    if (successRate >= SUCCESS_RATE_GOOD) {return 'good';}
    if (successRate >= SUCCESS_RATE_ACCEPTABLE) {return 'acceptable';}
    return 'poor';
  },
};
